import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaBars,
  FaTimes,
  FaPhone,
  FaEnvelope,
  FaChevronDown,
  FaWhatsapp,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import ThemeLanguageSwitcher from "./ThemeLanguageSwitcher";

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setMobileDropdown(null);
  }, [location]);

  const navLinks = [
    {
      name: t("nav.home"),
      path: "/",
    },
    {
      name: t("nav.aboutUs"),
      path: "/about",
      dropdown: [
        { name: t("nav.whoWeAre"), path: "/about/who-we-are" },
        { name: t("nav.ourStory"), path: "/about/our-story" },
        { name: t("nav.rnd"), path: "/about/rnd" },
        { name: t("nav.news"), path: "/about/news" },
      ],
    },
    {
      name: t("nav.ourProducts"),
      path: "/products",
      dropdown: [
        {
          name: t("nav.kingCoconutWater"),
          path: "/products/king-coconut-water",
        },
        {
          name: t("nav.cannedBottleFruit"),
          path: "/products/canned-bottle-fruit",
        },
        {
          name: t("nav.jackfruitBananaBlossom"),
          path: "/products/jackfruit-banana-blossom",
        },
        {
          name: t("nav.dehydratedFruits"),
          path: "/products/dehydrated-fruits",
        },
        { name: t("nav.coconutproducts"), path: "/products/coconut-products" },
        { name: t("nav.fruitJuicePulp"), path: "/products/fruit-juice-pulp" },
        { name: t("nav.fruitPowder"), path: "/products/fruit-powder" },
        { name: t("nav.spices"), path: "/products/spices" },
      ],
    },
    {
      name: t("nav.farmerCommunity"),
      path: "/farmer-community",
    },
    {
      name: t("nav.qualityTraceability"),
      path: "/quality-sustainability",
    },
    {
      name: t("nav.insights"),
      path: "/insights",
    },
    {
      name: t("nav.csr"),
      path: "/csr",
    },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary-700 text-white py-2 text-sm">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4 md:gap-6">
            <a
              href="mailto:info@worganaturals.com"
              className="hidden md:flex items-center gap-2 hover:text-primary-200 transition"
            >
              <FaEnvelope className="text-xs" />
              <span>{t("nav.email")}</span>
            </a>
            <a
              href="tel:+94713992673"
              className="flex items-center gap-2 hover:text-primary-200 transition"
            >
              <FaPhone className="text-xs" />
              <span className="hidden sm:inline">{t("nav.phone")}</span>
            </a>
            <a
              href="https://wa.me/94773788508"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-primary-200 transition"
            >
              <FaWhatsapp className="text-xs" />
              <span className="hidden sm:inline">{t("nav.whatsapp")}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-primary-100 hidden md:inline">
              {t("nav.topBarTagline")}
            </span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white dark:bg-dark-800 shadow-lg py-3"
            : "bg-white/95 dark:bg-dark-800/95 backdrop-blur-sm py-4"
        }`}
        style={{ top: "36px" }}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo - Left Side */}
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/images/logo.png"
                alt={t("nav.logoAlt")}
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("nav.companyName")}
                </h1>
                <p className="text-xs text-primary-600 dark:text-primary-400">
                  {t("nav.companyTagline")}
                </p>
              </div>
            </Link>

            {/* Desktop Navigation - Center to Right */}
            <div className="hidden lg:flex items-center gap-1 flex-1 justify-end mr-6">
              {navLinks.map((link, index) => (
                <div
                  key={index}
                  className="relative"
                  onMouseEnter={() => link.dropdown && setActiveDropdown(index)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.dropdown ? (
                    <button
                      className={`px-3 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 text-sm ${
                        isActive(link.path)
                          ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                          : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-700"
                      }`}
                    >
                      {link.name}
                      <FaChevronDown
                        className={`text-xs transition-transform ${
                          activeDropdown === index ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                        isActive(link.path)
                          ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                          : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-700"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}

                  {/* Dropdown Menu */}
                  {link.dropdown && (
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-gray-100 dark:border-dark-700 py-2"
                        >
                          {link.dropdown.map((item, idx) => (
                            <Link
                              key={idx}
                              to={item.path}
                              className="block px-6 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              <ThemeLanguageSwitcher />
              <Link
                to="/contact"
                className="hidden lg:block bg-primary-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm"
              >
                {t("nav.contact")}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden text-gray-700 dark:text-gray-300 text-2xl"
              >
                {isOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white dark:bg-dark-800 border-t border-gray-200 dark:border-dark-700"
            >
              <div className="container mx-auto px-4 py-4 space-y-2">
                {navLinks.map((link, index) => (
                  <div key={index}>
                    {link.dropdown ? (
                      <>
                        <button
                          onClick={() =>
                            setMobileDropdown(
                              mobileDropdown === index ? null : index
                            )
                          }
                          className="w-full flex items-center justify-between px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg"
                        >
                          <span>{link.name}</span>
                          <FaChevronDown
                            className={`text-xs transition-transform ${
                              mobileDropdown === index ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {mobileDropdown === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 space-y-1"
                            >
                              {link.dropdown.map((item, idx) => (
                                <Link
                                  key={idx}
                                  to={item.path}
                                  className="block px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg"
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={link.path}
                        className="block px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg"
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}
                <Link
                  to="/contact"
                  className="block w-full text-center bg-primary-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  {t("nav.contact")}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
